import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Grid, Paper, Stack, IconButton, Tooltip, 
    CircularProgress, Fade, Select, MenuItem,
    FormControl, InputLabel, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Chip, Button
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Refresh as RefreshIcon,
    People as ExpectedIcon,
    CheckCircle as PresentIcon,
    AccessTime as LateIcon,
    Cancel as AbsentIcon,
    EventNote as LeaveIcon,
    FileDownload as DownloadIcon,
    PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AppHeader from '../../components/AppHeader';
import { buildHierarchicalList } from '../../components/staff/constants';
import { fetchDailyAttendanceSummary, buildDailyAttendanceSummaryExcelUrl } from '../../services/webReportsService';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { myanmarFontBase64 } from '../../utils/myanmarFontBase64';

// Styling colors
const STATUS_COLORS = {
    PRESENT: { bg: '#e0f5ec', text: '#0e7490', label: 'Present' },
    LATE: { bg: '#fffbeb', text: '#b45309', label: 'Late' },
    INCOMPLETE: { bg: '#fef3c7', text: '#d97706', label: 'Incomplete' },
    ABSENT: { bg: '#fee2e2', text: '#b91c1c', label: 'Absent' },
    ON_LEAVE: { bg: '#e0e7ff', text: 'primary.main', label: 'On Leave' },
    REST_DAY: { bg: '#f1f5f9', text: '#475569', label: 'Rest Day' },
    HOLIDAY: { bg: '#f3e8ff', text: '#6b21a8', label: 'Holiday' },
    OFF: { bg: '#f1f5f9', text: '#475569', label: 'Off' }
};

export default function DailyAttendanceSummary({ setIsLoggedIn }) {
    const navigate = useNavigate();
    const tenant = useMemo(() => localStorage.getItem("tenant") || "", []);
    const token = useMemo(() => sessionStorage.getItem("token") || "", []);

    // Filter States
    const todayStr = useMemo(() => {
        const d = new Date();
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    }, []);

    const [date, setDate] = useState(todayStr);
    const [branchId, setBranchId] = useState("");
    const [orgNodeId, setOrgNodeId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL"); // 'ALL', 'PRESENT', 'LATE', 'ABSENT', 'ON_LEAVE'

    // Metadata States
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);

    // Data States
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [reportData, setReportData] = useState(null);

    // Fetch branch and department lists
    useEffect(() => {
        const fetchMetadata = async () => {
            const config = {
                withCredentials: true,
                headers: {
                    "X-Tenant": tenant,
                    "Authorization": `Bearer ${token}`
                }
            };
            try {
                const [branchRes, nodeRes] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/branches`, config),
                    axios.get(`${process.env.REACT_APP_API_URL1}/som/nodes`, config)
                ]);

                if (Array.isArray(branchRes.data?.data)) setBranches(branchRes.data.data);
                if (Array.isArray(nodeRes.data?.data)) setDepartments(buildHierarchicalList(nodeRes.data.data));
            } catch (err) {
                console.error("Failed to load metadata filters:", err);
            }
        };

        if (tenant && token) {
            fetchMetadata();
        }
    }, [tenant, token]);

    // Fetch attendance summary payload
    const loadReportData = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const res = await fetchDailyAttendanceSummary({
                date,
                branch_id: branchId || undefined,
                org_node_id: orgNodeId || undefined
            });

            if (res.success && res.data) {
                setReportData(res.data);
            } else {
                throw new Error(res.message || "Failed to load report data");
            }
        } catch (err) {
            console.error("Error loading daily attendance report:", err);
            setError(err.message || "Failed to load daily attendance summary.");
            setReportData(null);
        } finally {
            setLoading(false);
        }
    }, [date, branchId, orgNodeId]);

    useEffect(() => {
        if (tenant) {
            loadReportData();
        }
    }, [tenant, loadReportData]);

    // Handle quick filters by clicking cards
    const handleCardClick = (filterType) => {
        setStatusFilter(prev => prev === filterType ? "ALL" : filterType);
    };

    const onExportExcel = async () => {
        setLoading(true);
        setError("");
        try {
            const url = buildDailyAttendanceSummaryExcelUrl({
                date,
                branch_id: branchId || undefined,
                org_node_id: orgNodeId || undefined
            });

            const res = await fetch(url, {
                credentials: "include",
                headers: { "X-Tenant": tenant },
            });

            if (!res.ok) {
                const text = await res.text();
                throw new Error(text || `HTTP ${res.status}`);
            }

            const blob = await res.blob();
            const a = document.createElement("a");
            const objectUrl = URL.createObjectURL(blob);
            a.href = objectUrl;
            a.download = `daily_attendance_summary_${date}.xlsx`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        } catch (e) {
            setError(e?.message || "Excel export failed");
        } finally {
            setLoading(false);
        }
    };

    const onExportPDF = () => {
        try {
            const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });

            if (myanmarFontBase64) {
                doc.addFileToVFS("Padauk-Regular.ttf", myanmarFontBase64);
                doc.addFont("Padauk-Regular.ttf", "Padauk", "normal");
                doc.addFont("Padauk-Regular.ttf", "Padauk", "bold");
            }
            const defaultFont = myanmarFontBase64 ? "Padauk" : "helvetica";

            doc.setFont(defaultFont, "bold");
            doc.setFontSize(16);
            doc.setTextColor(30, 41, 59);
            doc.text("Daily Attendance Summary Report", 14, 15);

            doc.setFont(defaultFont, "normal");
            doc.setFontSize(10);
            doc.setTextColor(100);
            doc.text(`Date: ${date} | Generated At: ${new Date().toLocaleDateString()}`, 14, 21);

            const tableHeaders = [
                "No.",
                "Employee ID",
                "Staff Name",
                "Department",
                "Branch",
                "Roster Shift",
                "Expected In/Out",
                "Clock-in",
                "Clock-out",
                "Status",
                "Lateness",
                "Remarks"
            ];

            const tableRows = filteredRows.map((row, index) => {
                const expInOut = row.schedule.expected_in ? `${row.schedule.expected_in} - ${row.schedule.expected_out}` : "OFF";
                return [
                    index + 1,
                    row.employee_id,
                    row.full_name,
                    row.dept_name || "-",
                    row.branch_name || "-",
                    row.schedule.shift_code || "-",
                    expInOut,
                    row.attendance.clock_in || "-",
                    row.attendance.clock_out || "-",
                    row.attendance.status,
                    row.attendance.late_minutes > 0 ? `${row.attendance.late_minutes} mins` : "-",
                    row.attendance.remark || "-"
                ];
            });

            autoTable(doc, {
                startY: 26,
                head: [tableHeaders],
                body: tableRows,
                theme: "striped",
                headStyles: { fillColor: [16, 185, 129], font: defaultFont, fontStyle: "bold" },
                styles: { fontSize: 8, font: defaultFont },
                columnStyles: {
                    0: { cellWidth: 10 },
                    1: { cellWidth: 20 },
                    2: { cellWidth: 35 },
                    3: { cellWidth: 25 },
                    4: { cellWidth: 25 },
                    5: { cellWidth: 18 },
                    6: { cellWidth: 25 },
                    7: { cellWidth: 18 },
                    8: { cellWidth: 18 },
                    9: { cellWidth: 20 },
                    10: { cellWidth: 18 },
                    11: { cellWidth: 40 }
                }
            });

            doc.save(`daily_attendance_summary_${date}.pdf`);
        } catch (e) {
            setError(e?.message || "PDF Export failed");
        }
    };

    // Filter rows based on status card selection and search input
    const filteredRows = useMemo(() => {
        if (!reportData || !reportData.rows) return [];
        return reportData.rows.filter(row => {
            // Status check
            const attStatus = row.attendance.status;
            let matchesStatus = true;

            if (statusFilter === "PRESENT") {
                matchesStatus = attStatus === "PRESENT" || attStatus === "LATE" || attStatus === "INCOMPLETE";
            } else if (statusFilter === "LATE") {
                matchesStatus = attStatus === "LATE";
            } else if (statusFilter === "ABSENT") {
                matchesStatus = attStatus === "ABSENT";
            } else if (statusFilter === "ON_LEAVE") {
                matchesStatus = attStatus === "ON_LEAVE";
            }

            // Search query check
            const name = (row.full_name || "").toLowerCase();
            const code = (row.staff_code || "").toLowerCase();
            const id = (row.employee_id || "").toLowerCase();
            const query = searchQuery.toLowerCase();
            const matchesSearch = name.includes(query) || code.includes(query) || id.includes(query);

            return matchesStatus && matchesSearch;
        });
    }, [reportData, statusFilter, searchQuery]);

    // Render helper for status chips
    const getStatusChip = (status) => {
        const config = STATUS_COLORS[status] || { bg: '#e2e8f0', text: '#334155', label: status };
        return (
            <Chip 
                label={config.label} 
                size="small" 
                sx={{ 
                    bgcolor: config.bg, 
                    color: config.text, 
                    fontWeight: 700,
                    borderRadius: 1.5,
                    border: `1px solid ${config.text}20`
                }} 
            />
        );
    };

    const summary = reportData?.summary || { totalExpected: 0, present: 0, late: 0, absent: 0, leave: 0 };

    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            <AppHeader setIsLoggedIn={setIsLoggedIn} />

            <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1600, mx: 'auto' }}>
                {/* Header Section */}
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: 4 }}>
                    <Box>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                            <IconButton onClick={() => navigate('/reports')} size="small" sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#0f172a', letterSpacing: '-0.02em' }}>Daily Attendance Summary</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ color: '#64748b', ml: 5.5 }}>Real-time snapshots of today's attendance check-ins, lateness, absences, and active leaves.</Typography>
                    </Box>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button
                            variant="outlined"
                            color="success"
                            size="small"
                            onClick={onExportExcel}
                            startIcon={<DownloadIcon />}
                            disabled={loading || !reportData || reportData.rows.length === 0}
                            sx={{ bgcolor: '#fff', textTransform: 'none', fontWeight: 700 }}
                        >
                            Export Excel
                        </Button>
                        <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={onExportPDF}
                            startIcon={<PdfIcon />}
                            disabled={loading || !reportData || reportData.rows.length === 0}
                            sx={{ bgcolor: '#fff', textTransform: 'none', fontWeight: 700 }}
                        >
                            Export PDF
                        </Button>
                        <Tooltip title="Refresh Snapshot">
                            <IconButton onClick={loadReportData} sx={{ bgcolor: '#fff', border: '1px solid #e2e8f0' }}><RefreshIcon /></IconButton>
                        </Tooltip>
                    </Stack>
                </Stack>

                {/* Filters Panel */}
                <Paper sx={{ 
                    p: 3, borderRadius: 4, mb: 4, 
                    border: '1px solid #e2e8f0', 
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)',
                    bgcolor: '#fff'
                }}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={3}>
                            <TextField
                                label="Target Date"
                                type="date"
                                size="small"
                                fullWidth
                                InputLabelProps={{ shrink: true }}
                                value={date}
                                onChange={(e) => setDate(e.target.value)}
                            />
                        </Grid>

                        <Grid item xs={12} sm={4.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="branch-select-label">Branch</InputLabel>
                                <Select
                                    labelId="branch-select-label"
                                    value={branchId}
                                    label="Branch"
                                    onChange={(e) => setBranchId(e.target.value)}
                                >
                                    <MenuItem value="">All Branches</MenuItem>
                                    {branches.map(b => (
                                        <MenuItem key={b.id} value={b.id}>{b.branch_name}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4.5}>
                            <FormControl fullWidth size="small">
                                <InputLabel id="dept-select-label">Department</InputLabel>
                                <Select
                                    labelId="dept-select-label"
                                    value={orgNodeId}
                                    label="Department"
                                    onChange={(e) => setOrgNodeId(e.target.value)}
                                >
                                    <MenuItem value="">All Departments</MenuItem>
                                    {departments.map(d => (
                                        <MenuItem key={d.id} value={d.id} sx={{ pl: 2 + (d._depth || 0) * 2 }}>
                                            {d._depth > 0 && <span style={{ color: '#94a3b8', marginRight: 4 }}>{'└'}</span>}
                                            {d.node_name}{d.level_type ? ` (${d.level_type})` : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>
                </Paper>

                {/* Loading State */}
                {loading ? (
                    <Box sx={{ height: '50vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
                        <CircularProgress size={40} sx={{ color: '#10b981' }} />
                        <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 600 }}>Fetching live daily presence snapshot...</Typography>
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4, bgcolor: '#fee2e2', color: '#991b1b', borderRadius: 4, border: '1px solid #fecaca' }}>
                        <Typography variant="h6" sx={{ fontWeight: 800 }}>Error Loading Summary</Typography>
                        <Typography variant="body2">{error}</Typography>
                    </Box>
                ) : (
                    <Fade in timeout={600}>
                        <Box>
                            {/* KPI Stat Cards (Interactive) */}
                            <Grid container spacing={3} sx={{ mb: 4 }}>
                                {/* 1. Expected Today */}
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Paper sx={{ 
                                        p: 3, borderRadius: 4, border: '1px solid #e2e8f0', 
                                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                        display: 'flex', alignItems: 'center', gap: 2.5
                                    }}>
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#e0f2fe', color: '#0284c7', display: 'flex' }}>
                                            <ExpectedIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>EXPECTED TODAY</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summary.totalExpected}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 2. Present (Active Filter Toggle) */}
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Paper 
                                        onClick={() => handleCardClick("PRESENT")}
                                        sx={{ 
                                            p: 3, borderRadius: 4, 
                                            border: statusFilter === "PRESENT" ? '2px solid #10b981' : '1px solid #e2e8f0', 
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                            display: 'flex', alignItems: 'center', gap: 2.5,
                                            cursor: 'pointer', transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#d1fae5', color: '#10b981', display: 'flex' }}>
                                            <PresentIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>CLOCKED IN</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summary.present}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 3. Late (Active Filter Toggle) */}
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Paper 
                                        onClick={() => handleCardClick("LATE")}
                                        sx={{ 
                                            p: 3, borderRadius: 4, 
                                            border: statusFilter === "LATE" ? '2px solid #f59e0b' : '1px solid #e2e8f0', 
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                            display: 'flex', alignItems: 'center', gap: 2.5,
                                            cursor: 'pointer', transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#ffedd5', color: '#f59e0b', display: 'flex' }}>
                                            <LateIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>LATE ARRIVALS</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summary.late}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 4. Absent (Active Filter Toggle) */}
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Paper 
                                        onClick={() => handleCardClick("ABSENT")}
                                        sx={{ 
                                            p: 3, borderRadius: 4, 
                                            border: statusFilter === "ABSENT" ? '2px solid #ef4444' : '1px solid #e2e8f0', 
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                            display: 'flex', alignItems: 'center', gap: 2.5,
                                            cursor: 'pointer', transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#fee2e2', color: '#ef4444', display: 'flex' }}>
                                            <AbsentIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>ABSENTEES</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summary.absent}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>

                                {/* 5. Leave (Active Filter Toggle) */}
                                <Grid item xs={12} sm={6} md={2.4}>
                                    <Paper 
                                        onClick={() => handleCardClick("ON_LEAVE")}
                                        sx={{ 
                                            p: 3, borderRadius: 4, 
                                            border: statusFilter === "ON_LEAVE" ? '2px solid #8b5cf6' : '1px solid #e2e8f0', 
                                            boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)',
                                            display: 'flex', alignItems: 'center', gap: 2.5,
                                            cursor: 'pointer', transition: 'transform 0.15s',
                                            '&:hover': { transform: 'scale(1.02)' }
                                        }}
                                    >
                                        <Box sx={{ p: 1.5, borderRadius: 3, bgcolor: '#f3e8ff', color: '#8b5cf6', display: 'flex' }}>
                                            <LeaveIcon fontSize="medium" />
                                        </Box>
                                        <Box>
                                            <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, display: 'block' }}>ON APPROVED LEAVE</Typography>
                                            <Typography variant="h4" sx={{ fontWeight: 900, color: '#1e293b' }}>{summary.leave}</Typography>
                                        </Box>
                                    </Paper>
                                </Grid>
                            </Grid>

                            {/* Search and Table Grid Container */}
                            <Paper sx={{ 
                                p: 3, borderRadius: 4, border: '1px solid #e2e8f0',
                                boxShadow: '0 4px 20px 0 rgba(0,0,0,0.02)',
                                display: 'flex', flexDirection: 'column'
                            }}>
                                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                                    <Box>
                                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#1e293b' }}>
                                            Roster & Attendance List
                                            {statusFilter !== "ALL" && (
                                                <Chip 
                                                    label={`Filtered: ${statusFilter}`} 
                                                    size="small" 
                                                    onDelete={() => setStatusFilter("ALL")}
                                                    sx={{ ml: 1.5, fontWeight: 700, bgcolor: '#f1f5f9', color: '#475569' }} 
                                                />
                                            )}
                                        </Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>Showing {filteredRows.length} of {reportData.rows.length} total staff</Typography>
                                    </Box>

                                    <TextField
                                        placeholder="Search by name, ID or code..."
                                        size="small"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        sx={{ width: { xs: '100%', sm: 300 } }}
                                    />
                                </Stack>

                                <TableContainer sx={{ maxHeight: 600, overflowY: 'auto' }}>
                                    <Table stickyHeader size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Employee ID</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Staff Name</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Department</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Branch</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}> Roster Shift</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Expected In/Out</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Clock-in</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Clock-out</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Status</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Lateness</TableCell>
                                                <TableCell sx={{ fontWeight: 700, bgcolor: '#f8fafc', color: '#475569' }}>Remarks</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredRows.length > 0 ? (
                                                filteredRows.map((row) => (
                                                    <TableRow key={row.staff_id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                                                        <TableCell sx={{ fontWeight: 600, color: '#334155' }}>{row.employee_id}</TableCell>
                                                        <TableCell sx={{ fontWeight: 700, color: '#1e293b' }}>{row.full_name}</TableCell>
                                                        <TableCell sx={{ color: '#475569' }}>{row.dept_name || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#475569' }}>{row.branch_name || '-'}</TableCell>
                                                        <TableCell sx={{ color: '#334155', fontWeight: 600 }}>{row.schedule.shift_code}</TableCell>
                                                        <TableCell sx={{ color: '#475569' }}>
                                                            {row.schedule.expected_in ? `${row.schedule.expected_in} - ${row.schedule.expected_out}` : 'OFF'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#0284c7', fontWeight: 700 }}>
                                                            {row.attendance.clock_in || '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#0284c7', fontWeight: 700 }}>
                                                            {row.attendance.clock_out || '-'}
                                                        </TableCell>
                                                        <TableCell>
                                                            {getStatusChip(row.attendance.status)}
                                                        </TableCell>
                                                        <TableCell sx={{ color: row.attendance.late_minutes > 0 ? '#b45309' : '#64748b', fontWeight: row.attendance.late_minutes > 0 ? 700 : 500 }}>
                                                            {row.attendance.late_minutes > 0 ? `${row.attendance.late_minutes} mins` : '-'}
                                                        </TableCell>
                                                        <TableCell sx={{ color: '#64748b', fontStyle: 'italic', fontSize: '0.85rem' }}>
                                                            {row.attendance.remark || '-'}
                                                        </TableCell>
                                                    </TableRow>
                                                ))
                                            ) : (
                                                <TableRow>
                                                    <TableCell colSpan={10} align="center" sx={{ py: 6 }}>
                                                        <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                                                            No roster or attendance records match your search query / active filters.
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Box>
                    </Fade>
                )}
            </Box>
        </Box>
    );
}
